import React, { useState, useEffect } from "react";
import client from "@/api/client";
import { AlertTriangle, CheckCircle2, Minus, TrendingDown, TrendingUp,
         ChevronDown, ChevronRight, Zap } from "lucide-react";

// ── Config ────────────────────────────────────────────────────────────────
const SCENARIO_CONFIG = {
  net_zero: {
    label:     "Net Zero 2050",
    abbr:      "Net Zero",
    icon:      "NZ",
    desc:      "Rapid policy action, $150/tCO₂e",
    headerBg:  "bg-emerald-600",
    headerText:"text-white",
    badge:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  delayed_transition: {
    label:     "Delayed Transition",
    abbr:      "Delayed",
    icon:      "DT",
    desc:      "Slow policy, fossil volatility $60/tCO₂e",
    headerBg:  "bg-amber-500",
    headerText:"text-white",
    badge:     "bg-amber-50 text-amber-700 border-amber-200",
  },
  high_warming: {
    label:     "High Warming 3°C+",
    abbr:      "High Warming",
    icon:      "HW",
    desc:      "Policy failure, physical risk $25/tCO₂e",
    headerBg:  "bg-red-600",
    headerText:"text-white",
    badge:     "bg-red-50 text-red-700 border-red-200",
  },
};

const EXPOSURE_CONFIG = {
  low:    { label: "Low Exposure",      bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-300", icon: CheckCircle2,    iconCls: "text-emerald-600" },
  medium: { label: "Moderate Exposure", bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-300",   icon: Minus,           iconCls: "text-amber-600" },
  high:   { label: "High Exposure",     bg: "bg-red-50",      text: "text-red-700",     border: "border-red-300",     icon: AlertTriangle,   iconCls: "text-red-600" },
};

function ExposureBadge({ level }) {
  const c = EXPOSURE_CONFIG[level] || EXPOSURE_CONFIG.low;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.text} ${c.bg} ${c.border}`}>
      <Icon size={11} className={c.iconCls} />
      {c.label}
    </span>
  );
}

function CostDelta({ value }) {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  const abs = Math.abs(value);
  const formatted = `$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (value < 0) return <span className="text-emerald-600 font-semibold">−{formatted}</span>;
  if (value > 0) return <span className="text-red-600 font-semibold">+{formatted}</span>;
  return <span className="text-slate-500">$0</span>;
}

// ── Main Component ────────────────────────────────────────────────────────
export default function StressTest({ assessmentId, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeScenario, setActiveScenario] = useState(null);
  const [showTable, setShowTable] = useState(true);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get(`/fi/stress-test/${assessmentId}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Stress test failed");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (assessmentId) load();
  }, [assessmentId]);

  if (loading) return (
    <div className="flex items-center gap-2 py-4 text-xs text-slate-400">
      <div className="w-3.5 h-3.5 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
      Running stress test scenarios...
    </div>
  );
  if (error) return (
    <div className="text-xs text-red-500 py-2">{error}</div>
  );
  if (!data) return null;

  const scenarios = data.scenarios || {};
  const scenarioKeys = ["net_zero", "delayed_transition", "high_warming"];

  const worstExp = data.worst_case?.exposure || "Low Exposure";
  const worstScenario = data.worst_case?.scenario;
  const worstCost = data.worst_case?.total_cost;

  return (
    <div className={`space-y-4 ${embedded ? "" : "p-0"}`} data-testid="stress-test-panel">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-[#059669]" />
          <span className="text-sm font-semibold text-[#0F172A]">Climate Stress Scenarios</span>
          <span className="text-xs text-slate-400">{data.baseline_tco2e?.toFixed(1)} tCO₂e baseline</span>
        </div>
        <button onClick={() => setShowTable(!showTable)} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
          {showTable ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {showTable ? "Collapse" : "Expand"}
        </button>
      </div>

      {/* Worst case summary bar */}
      <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${EXPOSURE_CONFIG[worstExp.toLowerCase().split(" ")[0]]?.bg || "bg-slate-50"} ${EXPOSURE_CONFIG[worstExp.toLowerCase().split(" ")[0]]?.border || "border-slate-200"}`}>
        <div>
          <span className="text-xs font-semibold text-slate-600">Worst case: </span>
          <span className="text-xs font-bold text-[#0F172A]">{worstScenario}</span>
          <span className="text-xs text-slate-500 ml-2">— est. ${(worstCost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr additional cost</span>
        </div>
        <ExposureBadge level={worstExp.toLowerCase().split(" ")[0]} />
      </div>

      {showTable && (
        <>
          {/* Side-by-side comparison table */}
          <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="stress-test-table">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-slate-400 font-semibold uppercase tracking-wider w-40">Metric</th>
                  {scenarioKeys.map(k => {
                    const cfg = SCENARIO_CONFIG[k];
                    return (
                      <th key={k} className={`px-4 py-2.5 border-b border-slate-200 text-center ${cfg.headerBg} ${cfg.headerText} font-bold`}>
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-sm font-bold">{cfg.abbr}</span>
                          <span className="text-xs opacity-80 font-normal">{cfg.desc}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {/* Carbon price */}
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-600">Carbon Price</td>
                  {scenarioKeys.map(k => (
                    <td key={k} className="px-4 py-3 text-center">
                      <span className="font-bold text-[#0F172A]">${scenarios[k]?.carbon_price_usd}/t</span>
                    </td>
                  ))}
                </tr>
                {/* Carbon cost */}
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-600">Carbon Cost / yr</td>
                  {scenarioKeys.map(k => (
                    <td key={k} className="px-4 py-3 text-center">
                      <span className="font-semibold text-red-600">+${(scenarios[k]?.carbon_cost_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </td>
                  ))}
                </tr>
                {/* Energy cost delta */}
                <tr className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-600">Energy Cost Δ / yr</td>
                  {scenarioKeys.map(k => (
                    <td key={k} className="px-4 py-3 text-center">
                      <CostDelta value={scenarios[k]?.energy_cost_delta_usd} />
                    </td>
                  ))}
                </tr>
                {/* Total additional */}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-4 py-3 text-slate-700">Total Additional / yr</td>
                  {scenarioKeys.map(k => {
                    const total = scenarios[k]?.total_additional_cost_usd || 0;
                    return (
                      <td key={k} className="px-4 py-3 text-center">
                        <span className={`font-bold text-sm ${total > 50000 ? "text-red-600" : total > 15000 ? "text-amber-700" : "text-[#0F172A]"}`}>
                          ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      </td>
                    );
                  })}
                </tr>
                {/* Per employee (if available) */}
                {scenarioKeys.some(k => scenarios[k]?.cost_per_employee_usd) && (
                  <tr className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500">Per Employee / yr</td>
                    {scenarioKeys.map(k => (
                      <td key={k} className="px-4 py-3 text-center text-slate-600">
                        {scenarios[k]?.cost_per_employee_usd
                          ? `$${scenarios[k].cost_per_employee_usd.toFixed(0)}`
                          : "—"}
                      </td>
                    ))}
                  </tr>
                )}
                {/* Exposure level */}
                <tr className="border-t-2 border-slate-200">
                  <td className="px-4 py-3 font-bold text-slate-700">Exposure Level</td>
                  {scenarioKeys.map(k => (
                    <td key={k} className="px-4 py-3 text-center">
                      <ExposureBadge level={scenarios[k]?.exposure_level || "low"} />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Per-scenario interpretation cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stress-interpretations">
            {scenarioKeys.map(k => {
              const sc  = scenarios[k] || {};
              const cfg = SCENARIO_CONFIG[k];
              const exp = EXPOSURE_CONFIG[sc.exposure_level || "low"];
              const isActive = activeScenario === k;
              return (
                <button
                  key={k}
                  onClick={() => setActiveScenario(isActive ? null : k)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    isActive ? `border-current ${exp.border}` : "border-transparent bg-slate-50 hover:bg-slate-100"
                  }`}
                  data-testid={`scenario-card-${k}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold text-white ${cfg.headerBg}`}>
                      {cfg.icon}
                    </span>
                    <ExposureBadge level={sc.exposure_level || "low"} />
                  </div>
                  <p className="text-xs font-semibold text-[#0F172A] mb-1">{cfg.label}</p>
                  {isActive ? (
                    <p className="text-xs text-slate-600 leading-relaxed">{sc.interpretation}</p>
                  ) : (
                    <p className="text-xs text-slate-400 line-clamp-2">{sc.interpretation?.substring(0, 80)}…</p>
                  )}
                  <p className={`text-xs mt-1 ${isActive ? "text-slate-400" : "text-[#059669]"}`}>
                    {isActive ? "Click to collapse" : "Click to read"}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Source footnote */}
          <p className="text-xs text-slate-400 leading-relaxed">
            Scenarios based on <strong>NGFS/IEA climate pathways</strong>. Carbon prices are indicative.
            Physical risk costs (insurance, supply chain, water stress) not included in energy cost delta.
            Not investment advice.
          </p>
        </>
      )}
    </div>
  );
}
